import { Injectable } from '@angular/core';

import { Tag } from '@cds/betting-offer/grouping/fixture-view';
import {
    EnhancedFixtureViewGroupingConfiguration,
    EventOptionGroup,
    EventOptionSet,
    EventSitemapSet,
} from '@frontend/sports/betting-offer/feature/model';
import { AngstromTagIdValues } from '@frontend/sports/betting-offer/feature/types';
import { isDefined } from '@frontend/sports/common/core/utils/extended-types';
import { groupBy, isEqual, uniqWith } from 'lodash-es';

@Injectable({ providedIn: 'root' })
export class EventSitemapGroupingFactory {
    getSitemapSet(
        optionSets: EventOptionSet[],
        grouping: EnhancedFixtureViewGroupingConfiguration,
        optionGroups: EventOptionGroup[],
    ): EventSitemapSet[] {
        const sets: EventSitemapSet[] = this.buildPrimaryLevelItems(optionSets);

        sets.forEach((s) => {
            const marketGroupsPerSet = Object.values(grouping.marketGroupsV2)
                .filter(isDefined)
                .filter((x) => (x.tag?.key ? x.tag.key === s.id : x.order === s.id));
            const marketGroupItems = marketGroupsPerSet?.map((m) => Object.values(m.marketGroupItems)).flat(1);

            if (marketGroupItems.length) {
                const children: EventSitemapSet[] = [];
                const hasTaggedGroupItems = marketGroupItems.some((m) => m?.tags.length !== 0);

                if (hasTaggedGroupItems) {
                    const optTags = optionGroups
                        .filter((x) => (s.groups ? s.groups.includes(x.detailedGrouping.marketSet) : x.detailedGrouping.marketSet === s.id))
                        .filter((x) => (s.id === AngstromTagIdValues.Sgp ? x.isBetBuilder : true))
                        .filter((x) => !!x.detailedGrouping.tags)
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        .map((x) => x.detailedGrouping.tags!);
                    const availableTags = uniqWith(optTags, isEqual);

                    marketGroupItems?.forEach((groupItem) => {
                        if (groupItem) {
                            const tags = [...groupItem.tags];

                            if (tags.length > 0 && this.tagsExist(tags, availableTags)) {
                                this.buildLowerLevelItems(children, tags, s.groups);
                            }
                        }
                    });
                }

                // eslint-disable-next-line no-param-reassign
                s.children = children;
            }
        });

        return sets.filter((x) => (x.id === AngstromTagIdValues.Sgp ? !!x.children?.length : true));
    }

    private buildPrimaryLevelItems(optionSets: EventOptionSet[]): EventSitemapSet[] {
        const sets: EventSitemapSet[] = [];
        const groupedSets = groupBy(
            optionSets.filter((x) => !!x.marketGroupTag?.key),
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            (os) => os.marketGroupTag!.key,
        );

        // eslint-disable-next-line guard-for-in
        for (const tag in groupedSets) {
            const current = groupedSets[tag];
            const set: EventSitemapSet = {
                id: tag,
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                name: current[0].marketGroupTag!.value || current[0].marketGroupTag!.key,
                groups: current.map((c) => c.id),
                children: [],
            };

            sets.push(set);
        }

        optionSets
            .filter((x) => !x.marketGroupTag || !x.marketGroupTag.key)
            .forEach((o) =>
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                sets.push({
                    ...o,
                    groups: [o.id],
                    children: [],
                } as EventSitemapSet),
            );

        return sets;
    }

    private buildLowerLevelItems(children: EventSitemapSet[], tags: Tag[], groups?: number[]): void {
        const keyExists = (key: string) => children.find((el) => el.id === key);
        const append = (tag: { key: string; value: string }) => {
            children.push({
                id: tag.key,
                name: tag.value || tag.key.toString(),
                children: [],
                groups,
            });
        };

        const currentTag = tags[0];
        if (!keyExists(currentTag.key)) {
            append(currentTag);
        }

        tags.shift();
        if (tags.length !== 0) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const nextLevelItem = children.find((el) => el.id === currentTag.key)!;
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            this.buildLowerLevelItems(nextLevelItem.children!, tags, groups);
        }
    }

    private tagsExist(tags: Tag[], optionGrouping: Tag[][]): boolean {
        return optionGrouping.some((optTags) => {
            if (optTags.length !== tags.length) {
                return false;
            }

            return tags.every((tag, index) => tag.key === optTags[index].key);
        });
    }
}
